import { useStorage } from "@platform/hooks";
import { useCallback } from "react";

import type { CarrierColumn, FinancialsTableResponse } from "@platform/api";

export interface UseFinancialsHiddenCarriersResult {
  reconcilePayload: (
    payload: FinancialsTableResponse,
  ) => FinancialsTableResponse;
  persistHiddenCarrier: (
    carriers: readonly CarrierColumn[],
    carrierId: string,
    hidden: boolean,
  ) => void;
}

export const useFinancialsHiddenCarriers = (
  rfpId: string,
): UseFinancialsHiddenCarriersResult => {
  const { getStorageItem, setStorageItem, removeStorageItem } =
    useStorage("local");
  const storageKey = `financials-hidden-carriers:${rfpId}`;

  const reconcilePayload = useCallback(
    (payload: FinancialsTableResponse): FinancialsTableResponse => {
      const persisted = getStorageItem<string[]>(storageKey);
      if (!persisted) {
        return payload;
      }
      const hidden = new Set(persisted);
      const carriers = payload.carriers.map((carrier) =>
        carrier.hidden === hidden.has(carrier.id)
          ? carrier
          : { ...carrier, hidden: hidden.has(carrier.id) },
      );

      if (carriers.every((c, idx) => c === payload.carriers[idx])) {
        return payload;
      }
      return { ...payload, carriers };
    },
    [getStorageItem, storageKey],
  );

  const persistHiddenCarrier = useCallback(
    (
      carriers: readonly CarrierColumn[],
      carrierId: string,
      hidden: boolean,
    ): void => {
      const hiddenIds = carriers
        .filter((carrier) =>
          carrier.id === carrierId ? hidden : carrier.hidden,
        )
        .map((carrier) => carrier.id);
      if (hiddenIds.length === 0) {
        removeStorageItem(storageKey);
      } else {
        setStorageItem(storageKey, hiddenIds);
      }
    },
    [setStorageItem, removeStorageItem, storageKey],
  );

  return { reconcilePayload, persistHiddenCarrier };
};
